import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Monitor, ArrowLeft } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const res = await api.post(endpoint, payload);
      loginWithToken(res.data.user, res.data.session_token);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(res.data.user?.role === "admin" ? "/admin" : "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[42%] border-r border-zinc-900 p-12 relative tech-grid">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors font-mono text-xs uppercase tracking-widest" data-testid="back-home-btn">
          <ArrowLeft size={16} /> Home
        </button>
        <div>
          <Monitor size={40} weight="duotone" className="text-[#0055FF] mb-6" />
          <h1 className="font-heading font-black text-5xl tracking-tighter leading-none">
            REMOTE<br />ACCESS,<br /><span className="text-[#0055FF]">SECURED.</span>
          </h1>
          <p className="text-zinc-500 mt-6 max-w-xs font-mono text-sm">
            One console to control every screen you're trusted with.
          </p>
        </div>
        <p className="font-mono text-xs text-zinc-700">NEXUSRDP · encrypted peer-to-peer</p>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="w-full max-w-sm"
        >
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Monitor size={26} weight="duotone" className="text-[#0055FF]" />
            <span className="font-heading font-black tracking-tighter">NEXUSRDP</span>
          </div>

          <h2 className="font-heading font-black text-3xl tracking-tighter mb-1">
            {mode === "login" ? "Sign in" : "Create account"}
          </h2>
          <p className="text-zinc-500 text-sm mb-8">
            {mode === "login" ? "Access your remote console." : "Start controlling screens in seconds."}
          </p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Name</label>
                <input
                  data-testid="name-input"
                  type="text" required value={form.name} onChange={update("name")}
                  className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
                  placeholder="Ada Lovelace"
                />
              </div>
            )}
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Email</label>
              <input
                data-testid="email-input"
                type="email" required value={form.email} onChange={update("email")}
                className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <label className="font-mono text-[11px] uppercase tracking-widest text-zinc-500">Password</label>
              <input
                data-testid="password-input"
                type="password" required value={form.password} onChange={update("password")}
                className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 px-4 py-3 text-white focus:border-[#0055FF] focus:ring-1 focus:ring-[#0055FF] outline-none transition-colors"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit" disabled={busy}
              data-testid="submit-auth-btn"
              className="w-full bg-white text-black font-mono text-sm uppercase tracking-widest py-3.5 hover:bg-zinc-200 transition-colors disabled:opacity-50"
            >
              {busy ? "..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-600">or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <button
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="w-full flex items-center justify-center gap-3 border border-zinc-800 bg-zinc-900 py-3.5 hover:border-zinc-600 transition-colors font-mono text-sm"
          >
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <p className="text-center mt-8 text-sm text-zinc-500">
            {mode === "login" ? "No account?" : "Already have an account?"}{" "}
            <button
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              data-testid="toggle-mode-btn"
              className="text-[#00E5FF] hover:underline"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
